/* biome-ignore-all lint/suspicious/noExplicitAny: legacy data structure */
import React, { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Card, Button, Typography, Space, message } from "antd"
import { PlusOutlined, SettingOutlined } from "@ant-design/icons"
import { settingService, type SettingItem } from "@/services/system/settingApi"
import { SettingsFilterForm } from "./components/SettingsFilterForm"
import { SettingsTable } from "./components/SettingsTable"
import { EditSettingModal } from "./components/EditSettingModal"
import { CreateSettingModal } from "./components/CreateSettingModal"

const { Title, Text } = Typography

export const SettingsPage: React.FC = () => {
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [filters, setFilters] = useState<{ search?: string; group?: string }>({})

  // Modals state
  const [createModalOpen, setCreateModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [selectedSetting, setSelectedSetting] = useState<SettingItem | null>(null)

  const {
    data: response,
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ["settings_list", page, limit, filters],
    queryFn: () =>
      settingService.list({
        page,
        limit,
        search: filters.search,
        group: filters.group === "all" ? undefined : filters.group,
      }),
  })

  const settingsList = response?.data || []
  const total = response?.paginate?.total || 0
  const groups = response?.groups || []

  const handleSearch = (values: { search?: string; group?: string }) => {
    setFilters(values)
    setPage(1)
  }

  const handleEdit = (record: SettingItem) => {
    setSelectedSetting(record)
    setEditModalOpen(true)
  }

  const handleDelete = async (record: SettingItem) => {
    try {
      await settingService.delete(record.id)
      message.success(`Đã xóa cấu hình "${record.name}" thành công!`)
      refetch()
    } catch (e: any) {
      message.error("Lỗi khi xóa cấu hình: " + (e.message || "Unknown error"))
    }
  }

  return (
    <div className="flex flex-col gap-4">
      {/* Header card */}
      <Card className="shadow-xs">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center">
              <SettingOutlined className="text-xl" />
            </div>
            <div>
              <Title level={4} className="!mb-0 text-gray-800">
                Quản lý Cấu hình Hệ thống (Settings)
              </Title>
              <Text type="secondary" className="text-xs">
                Quản lý tập trung các cấu hình động, danh sách bộ từ nổi bật, người khác cũng đang học và các thiết lập toàn hệ thống.
              </Text>
            </div>
          </div>

          <Space>
            <Button
              type="primary"
              icon={<PlusOutlined />}
              onClick={() => setCreateModalOpen(true)}
            >
              Tạo cấu hình mới
            </Button>
          </Space>
        </div>
      </Card>

      {/* Filter bar */}
      <SettingsFilterForm
        groups={groups}
        onSearch={handleSearch}
        loading={isFetching}
        initialValues={filters}
      />

      {/* Table list */}
      <Card className="shadow-xs">
        <SettingsTable
          data={settingsList}
          loading={isFetching}
          pagination={{
            current: page,
            pageSize: limit,
            total,
            showSizeChanger: true,
            pageSizeOptions: ["10", "20", "50", "100"],
            onChange: (p, l) => {
              setPage(p)
              setLimit(l)
            },
            showTotal: (totalCount) => `Tổng số ${totalCount} cấu hình`,
          }}
          onEdit={handleEdit}
          onDelete={handleDelete}
        />
      </Card>

      {/* Create Modal */}
      <CreateSettingModal
        open={createModalOpen}
        groups={groups}
        onClose={() => setCreateModalOpen(false)}
        onSuccess={() => refetch()}
      />

      {/* Edit Modal */}
      <EditSettingModal
        open={editModalOpen}
        setting={selectedSetting}
        onClose={() => {
          setEditModalOpen(false)
          setSelectedSetting(null)
        }}
        onSuccess={() => refetch()}
      />
    </div>
  )
}

export default SettingsPage
